import styled from '@emotion/styled/macro'
import { Box, Drawer as MuiDrawer, Popper } from '@mui/material'
import theme from 'theme'

export const Drawer = styled(MuiDrawer)`
  .MuiPaper-root {
    background: ${theme.colors.surfaceContainerBackground};
    min-width: 351px;
    width: 490px;
    border-left: 1px solid ${theme.colors.strokePrimary};
    /* No right padding: the scroll area owns that edge so its bar rides near the panel border. */
    padding: ${theme.spacing.xLarge}px 0 ${theme.spacing.xLarge}px ${theme.spacing.xLarge}px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.large}px;

    .pane-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${theme.spacing.small}px;
      flex-shrink: 0;
      padding-right: ${theme.spacing.xLarge}px;

      .pane-title {
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.xxxxLarge};
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 42px;
        color: ${theme.colors.menuItemText};
      }

      /* The shared icon hardcodes stroke="#fff"; a CSS stroke overrides it and resolves the token. */
      .close-icon svg path {
        stroke: ${theme.colors.foreground};
      }
    }

    /* Bar styling comes from the shared .styledScrollbar class; the inset is this margin, which
       keeps the content's 40px gutter (32 padding + 8 margin) while the bar sits near the edge. */
    .pane-sections {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing.large}px;
      padding-right: ${theme.spacing.large}px;
      margin-right: ${theme.spacing.half}px;
    }
  }
`

export const SectionWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md}px;

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing.small}px;
    min-height: 24px;

    .section-label {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 22px;
      color: ${theme.colors.textCaption};
    }

    /* The design draws the count as bare text beside a chevron, not as a filled chip. */
    .section-badge {
      display: inline-flex;
      align-items: center;
      gap: ${theme.spacing.half}px;
      background: none;
      color: ${theme.colors.textCaption};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 22px;
      white-space: nowrap;
    }

    .section-badge.is-toggle {
      padding: 0;
      border: none;
      cursor: pointer;

      &:hover {
        color: ${theme.colors.menuItemText};
      }

      &:focus-visible {
        outline: 2px solid ${theme.colors.cta};
        outline-offset: 1px;
      }

      /* ChevronRight is intrinsically 24px and paints via a fill attribute, so the box has to be
         constrained and the colour overridden as CSS — an attribute would win over inheritance. */
      .badge-chevron {
        display: inline-flex;
        transition: transform 150ms ease;

        &.is-open {
          transform: rotate(90deg);
        }

        svg {
          width: ${theme.iconSize.small}px;
          height: ${theme.iconSize.small}px;
        }

        path {
          fill: ${theme.colors.mainSecondary};
        }
      }
    }
  }

  /* Two fixed columns, 247 + 12 + 151 = the 410 content width, so logos line up down the panel. */
  .value-row {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md}px;
    min-height: 24px;

    .value-text {
      flex: 1;
      min-width: 0;
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 20px;
      color: ${theme.colors.menuItemText};
      overflow-wrap: anywhere;
    }
  }

  /* The reporting instances, one per row under the section header. */
  .instance-list {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.xSmall}px;
  }

  /* The values other members reported, listed under the one the merge resolved to. */
  .value-list {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.xSmall}px;
    padding-top: ${theme.spacing.xSmall}px;

    .value-row.is-other .value-text {
      color: ${theme.colors.devicesSecondaryText};
    }
  }
`

export const LogoRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing.md}px;
  width: 151px;
  flex-shrink: 0;

  /* The Source section runs its logos from the left edge instead of the value column. */
  &.is-inline {
    width: auto;
    justify-content: flex-start;
  }

  img {
    width: 24px;
    height: 24px;
    border-radius: ${theme.attributes.borderRadius.small};
  }

  .logo-overflow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: ${theme.attributes.borderRadius.small};
    background: ${theme.colors.tooltipBg};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 20px;
    color: ${theme.colors.menuItemText};
  }
`

export const SourcePopperCard = styled(Popper)`
  z-index: 1400;

  .popper-card {
    background: ${theme.colors.devicesPopupBg};
    border: 1px solid ${theme.colors.strokePrimary};
    border-radius: ${theme.attributes.borderRadius.secondary};
    box-shadow: ${theme.attributes.boxShadow.secondary};
    padding: ${theme.spacing.xSmall}px;
    display: flex;
    flex-direction: column;
    min-width: 240px;
  }

  .product-row {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
    padding: ${theme.spacing.xSmall}px;
    border-radius: ${theme.attributes.borderRadius.small};
    text-decoration: none;
    color: inherit;

    img {
      width: 24px;
      height: 24px;
      border-radius: ${theme.attributes.borderRadius.small};
      flex-shrink: 0;
    }

    .product-names {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .product-title {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-weight: ${theme.typography.fontWeight.bold};
      line-height: 20px;
      color: ${theme.colors.menuItemText};
    }

    .product-instance {
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 16px;
      color: ${theme.colors.devicesSecondaryText};
    }

    .origin-icon {
      display: inline-flex;
      color: ${theme.colors.devicesSecondaryText};
      flex-shrink: 0;
    }
  }

  a.product-row:hover {
    background: ${theme.colors.menuItemHover};

    .origin-icon {
      color: ${theme.colors.cta};
    }
  }

  a.product-row:focus-visible {
    outline: 2px solid ${theme.colors.cta};
    outline-offset: -2px;
  }
`
